/**
 * The note behind the signed-out card (crossmarc/steward-app#2432).
 *
 * "Sign out" on the snapshot form and on the onboarding form used to drop the person on the
 * form's cover or on `/login` with nothing to say that the sign-out had worked. Both forms now
 * leave this note just before they call the app's full sign-out (`useAuth().signOut()`, which
 * is unchanged, as is every auth, Cognito and logout-URL setting), and the page the person
 * comes back to reads it and shows a card instead of the usual page.
 *
 * WHY THE NOTE SURVIVES THE SIGN-OUT. Cognito's `/logout` is a top-level redirect that returns
 * to `{origin}/login` on the same host (`auth/signOutReturn.ts`, which records the registered
 * address), so the page that reads the note has the origin that wrote it, and `sessionStorage`
 * is kept per origin for the life of the tab. A firm's own host is its own origin, so a note is
 * written and read on one host and never carried to another.
 *
 * WHAT THE NOTE HOLDS, and nothing else: which form, an identifier of the firm, whether the form
 * was sent, and when it was written. No email address, no name, no answer. Because storage can
 * be written by hand, the reader never trusts the firm in it: the page passes in the firm it has
 * itself resolved ({@link peekSignedOutCard}'s `expectedFirm`) and a note naming any other firm
 * shows nothing. The card is therefore never branded with a firm the page did not resolve.
 *
 *  - The snapshot form's firm is `firm.firmId`, as resolved from the slug or the host.
 *  - The onboarding form returns to `/login`, which resolves a firm from the host only, so its
 *    firm is the compiled firm of this host ({@link hostFirm}) or `null` on the shared host.
 *
 * WHICH DOCUMENT WROTE IT (#2432 round two). `endCognitoSession` clears the local token store
 * BEFORE it redirects to Cognito's `/logout`, so `ProtectedRoute` renders `/login` in the SAME
 * document first. A reader that consumed the note there left the page Cognito returns to with
 * nothing to read (seen on deployed dev, 2026-10-16). Each note therefore carries the id of the
 * document that wrote it ({@link DOCUMENT_ID}, random, made once when this module loads). A
 * note written by THIS document is neither shown nor discarded, because this document is about
 * to navigate away; a note from another document is read and discarded as before. A note with
 * no id (written before this field existed) counts as another document's.
 *
 * Every storage access is in a try/catch. A browser that refuses storage shows the page it
 * showed before this existed. The note is ignored after {@link MAX_AGE_MS} and is removed by
 * the page that reads it ({@link discardSignedOutCard}).
 */

import { advisorFirmDefForHostname, type AdvisorFirmDef } from '@steward-app/steward-types';

const KEY = 'steward.signout.card.v1';

/** Long enough for the round trip through the sign-in service, short enough not to outlive it. */
export const MAX_AGE_MS = 5 * 60 * 1000;

function newDocumentId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

/** Identifies this document (page load). Replaced only by {@link setDocumentIdForTests}. */
let DOCUMENT_ID = newDocumentId();

/** Test seam: models a new document (the page Cognito returns to) with a new id. */
export function setDocumentIdForTests(id: string): void {
  DOCUMENT_ID = id;
}

export type SignedOutForm = 'snapshot' | 'onboarding';

export interface SignedOutNote {
  form: SignedOutForm;
  /** The firm the form belonged to, or `null` where the page resolves none (the shared host). */
  firm: string | null;
  /** The form had been sent when the person signed out. */
  sent: boolean;
  /** The person signed out from the requests page (steward-app#2473): the card says their documents are with the firm. */
  requests?: boolean;
}

/** The compiled firm of the host serving this page, or `null` (the shared host). */
export function hostFirm(hostname: string = window.location.hostname): AdvisorFirmDef | null {
  try {
    return advisorFirmDefForHostname(hostname);
  } catch {
    return null;
  }
}

/** Called just before signing out. Replaces any earlier note. */
export function rememberSignedOutCard(note: SignedOutNote, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      form: note.form,
      firm: note.firm,
      sent: note.sent === true,
      // Left out unless true, so a note from any other sign-out keeps its shape.
      ...(note.requests === true ? { requests: true } : {}),
      at: now,
      doc: DOCUMENT_ID,
    }));
  } catch {
    // Storage refused: the person sees the page they saw before this card existed.
  }
}

/** The sign-out did not happen (it threw): the note is withdrawn, whichever form wrote it. */
export function forgetSignedOutCard(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored, then.
  }
}

/**
 * Whether a fresh note for this form and this firm is waiting, and whether the form was sent.
 * Does not remove it (a render may run twice), see {@link discardSignedOutCard}. `null` for a
 * missing, stale, malformed or other-form note and for a note naming a firm other than
 * `expectedFirm`.
 */
export function peekSignedOutCard(
  form: SignedOutForm,
  expectedFirm: string | null,
  now: number = Date.now(),
): { sent: boolean; requests?: true } | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    if (parsed['doc'] === DOCUMENT_ID) return null;
    if (parsed['form'] !== form) return null;
    const at = parsed['at'];
    if (typeof at !== 'number' || !Number.isFinite(at) || now - at < 0 || now - at > MAX_AGE_MS) return null;
    const firm = parsed['firm'];
    if (firm !== null && typeof firm !== 'string') return null;
    if (firm !== expectedFirm) return null;
    const sent = parsed['sent'];
    if (typeof sent !== 'boolean') return null;
    return parsed['requests'] === true ? { sent, requests: true } : { sent };
  } catch {
    return null;
  }
}

/**
 * Whether this form's sign-out is under way in THIS document: a fresh note for `form`, written
 * by this document, is stored. `endCognitoSession` clears the local store before it redirects,
 * so `/login` renders in the writing document for up to the discovery timeout; the page shows
 * a blank frame and applies no reload meanwhile, so nothing can race the redirect to Cognito.
 * Read, not remembered: a sign-out that throws calls {@link forgetSignedOutCard}, and the next
 * render then shows the ordinary page.
 */
export function leavingThisDocument(form: SignedOutForm, now: number = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Record<string, unknown> | null;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
    if (parsed['doc'] !== DOCUMENT_ID || parsed['form'] !== form) return false;
    const at = parsed['at'];
    return typeof at === 'number' && Number.isFinite(at) && now - at >= 0 && now - at <= MAX_AGE_MS;
  } catch {
    return false;
  }
}

/**
 * Removes the note once the page that reads it has mounted. Leaves a fresh note for the OTHER
 * form alone (each form's page removes its own); removes anything this form's page cannot use,
 * a stale, malformed or other-firm note included, so it cannot show on a later visit.
 */
export function discardSignedOutCard(form: SignedOutForm, now: number = Date.now()): void {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return;
    let keep = false;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown> | null;
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && parsed['doc'] === DOCUMENT_ID) {
        // Written by this document, which is about to navigate to the sign-in service.
        return;
      }
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && parsed['form'] !== form) {
        const at = parsed['at'];
        keep = (parsed['form'] === 'snapshot' || parsed['form'] === 'onboarding')
          && typeof at === 'number' && now - at >= 0 && now - at <= MAX_AGE_MS;
      }
    } catch {
      keep = false;
    }
    if (!keep) sessionStorage.removeItem(KEY);
  } catch {
    // Storage refused: nothing to remove.
  }
}
